/**
 * PRD-RTE-PARITY §TR1 — Single-source factory for the textbox HTML element.
 *
 * Both the SVG-foreignObject view-mode renderer and the contentEditable
 * editor mount the same DOM element produced here. Centralising the style
 * computation eliminates the edit/view divergence (G1, G2) that previously
 * existed between `textbox-svg-renderer.ts` (which had its own `BASE_STYLE`
 * and per-box concatenation) and `textbox-editor.controller.ts` (which used
 * a bare `document.createElement('div')` with no `obj`-derived inline style).
 *
 * The factory returns a detached element — callers position it (the SVG
 * path serializes via `XMLSerializer`; the editor mounts via `appendChild`)
 * and add path-specific overrides on top.
 */
import { generateHTML } from '@tiptap/core';

import type { TextBox, VerticalAlign } from './textbox.interface.js';
import { TEXTBOX_EXTENSIONS } from './tiptap-extensions.js';
import { maxRunFontSizePxFromJson, type StrutJsonNode } from './textbox-strut-fix.js';
import { BOARD_TEXT_LINE_HEIGHT } from '../../constants/whiteboard-config.constants.js';

// Board-Hausschrift D-DIN mit System-Fallback (siehe STICKY_FONT_FAMILY).
const FONT_FAMILY = '"D-DIN", system-ui, -apple-system, sans-serif';
/** Just the board house font's own family name (no fallback stack) — the
 *  needle `board-font-embed.helper.ts` matches consumer `@font-face` rules
 *  against for the view-mode raster's font-embedding bugfix (2026-08-31). */
export const BOARD_FONT_FAMILY_NAME = 'D-DIN';

/**
 * O2D.W-PRD-038 — the edit-overlay / parity wrapper line-height (unitless).
 *
 * This is the single source shared by the textbox HTML wrapper built here
 * (`buildTextboxHtmlElement`, used by BOTH the SVG view path and the edit
 * overlay) AND the per-run scaled inner `Decoration.inline` in
 * `textbox-editor.controller.ts` (T2.3). Keeping both on ONE constant is what
 * lets the scaled inner span drive the edit-overlay line box on zoom-out once
 * the outer `font-size` strut is neutralised (see `EDIT_TEXTBOX_EXTENSIONS`).
 *
 * Since Anke, 2026-10-03 the same value as every other board text
 * (`BOARD_TEXT_LINE_HEIGHT`, 1.2) — it used to be 1.4, and the checklist
 * hotspot constant `TEXTBOX_LINE_HEIGHT` a separate 1.25.
 */
export const EDIT_TEXTBOX_LINE_HEIGHT = BOARD_TEXT_LINE_HEIGHT;

/**
 * Bugreport 2026-08-15 (user) — the checkbox's world-unit width must be a
 * direct function of the item's own world-unit font-size:
 * `checkbox width = font-size × factor`. `1` keeps the box exactly as tall
 * as the text it labels.
 *
 * Bugreport 2026-08-16 (user, follow-up): also reused, via `CHECKLIST_BOX_SIZE`
 * below, as the width of the bullet/ordered-list marker box in
 * `applyListMarkers` — the "one shared factor across all 3 list kinds" the
 * user asked for is this literal constant, not three numbers that happen to
 * match today.
 */
export const CHECKLIST_BOX_SIZE_FACTOR = 1;

/**
 * O2D.W-PRD-040 §TR3 — the checklist item gap (checkbox ↔ text), as a factor
 * of the item's own world-unit font-size (same formula as the checkbox
 * itself): `gap = font-size × factor`.
 *
 * Bugreport 2026-08-16 (user, follow-up): also reused, via `CHECKLIST_ITEM_GAP`
 * below, as the gap between a bullet/ordered-list marker and its text in
 * `applyListMarkers` — see the note on `CHECKLIST_BOX_SIZE_FACTOR` above.
 */
export const CHECKLIST_ITEM_GAP_FACTOR = 1;

/**
 * Bugreport 2026-08-15 (user, follow-up) — "der Einzug bei den Listen (…)
 * muss ebenfalls mit einem Faktor an die Weltweiteinheit der Schriftgröße
 * gebunden sein, und zwar bei allen 3 Listen mit demselben Faktor, so dass
 * bei allen 3 Listen gleicher Abstand bei gleicher Schriftgröße rauskommt."
 *
 * The checklist has no separate "indent" property — the distance from the
 * textbox's left edge to where the TEXT starts is `CHECKLIST_BOX_SIZE_FACTOR`
 * (the checkbox) + `CHECKLIST_ITEM_GAP_FACTOR` (the gap after it) world-units
 * of font-size. For bullet/ordered lists to land the TEXT at that exact same
 * distance, their `<li>`'s own `padding-left` (see `LIST_INDENT` below) must
 * equal this SAME sum — one shared factor, not three independently-tuned
 * ones that happen to agree today and silently drift apart tomorrow.
 */
export const LIST_INDENT_FACTOR = CHECKLIST_BOX_SIZE_FACTOR + CHECKLIST_ITEM_GAP_FACTOR;

/**
 * SINGLE JS source for the checklist `gap` value, consumed by the view-path
 * inline styles in `applyChecklistInlineStyles` below. CSS cannot import a TS
 * const, so the EDIT/overlay side in `whiteboard.css` mirrors this literal
 * with a cross-referencing comment; a drift-guard unit test asserts the two
 * stay equal (FC4). Only the GAP is constant-backed — the other layout values
 * (`list-style:none`, `display:flex`, `align-items:baseline`, `flex:1`,
 * `min-width:0`) are mirrored by convention across the twin sites (DD3).
 */
export const CHECKLIST_ITEM_GAP = `${CHECKLIST_ITEM_GAP_FACTOR}em`;

/**
 * Left indent (gutter) for GENERIC bullet / ordered lists, expressed in `em`
 * (= `LIST_INDENT_FACTOR` × the element's own font-size) so it scales with
 * the (zoom-scaled) font — exactly like the checklist's checkbox+gap, which
 * this is deliberately kept equal to (see `LIST_INDENT_FACTOR`). The browser
 * user-agent default is a FIXED `40px` `padding-left`, which does NOT track
 * viewport zoom in the SVG raster (font-size and textbox padding are
 * multiplied by `scale`; a fixed px indent is not), so bullet/ordered indents
 * appeared to shrink as you zoomed in.
 * The EDIT/overlay side in `whiteboard.css` mirrors this literal; a drift-guard
 * unit test asserts the two stay equal. The taskList keeps a 0 gutter (the
 * checkbox replaces the marker) — see `applyChecklistInlineStyles`.
 */
export const LIST_INDENT = `${LIST_INDENT_FACTOR}em`;

/**
 * Anke, 2026-07-19 — the clickable launcher rendered after every detected URL.
 *
 * The glyph is a plain arrow character, NOT an image: it has to survive being
 * serialized into the SVG raster, where an external asset reference would not
 * load (the raster is a self-contained `data:` URL).
 */
export const LINK_ICON_GLYPH = '↗';

/**
 * Marks the launcher element AND carries its target URL. The measuring pass
 * reads both from this one attribute, so there is a single place where the
 * icon-to-URL pairing lives.
 */
export const LINK_ICON_ATTR = 'data-o2d-link';

/** Breathing space either side of the launcher, in `em` so it scales with the text. */
export const LINK_ICON_GAP = '0.15em';

/**
 * Launcher size relative to the text it follows. Slightly smaller so the arrow
 * reads as an attachment to the address rather than as a character of it.
 */
export const LINK_ICON_SCALE = 0.85;

/**
 * Size (width = height) of the checklist's native `<input type="checkbox">`,
 * expressed in `em` (= `CHECKLIST_BOX_SIZE_FACTOR` × the element's own
 * font-size) so it scales with the zoom-scaled font like the rest of the
 * checklist. A native checkbox keeps its OWN user-agent font-size, so `em`
 * would otherwise resolve against that fixed size and never scale — the
 * twin sites therefore also force `font-size: inherit` on the input before
 * applying this. Resolving correctly against the ITEM's own (possibly
 * per-run-mixed) font-size — not the textbox's unrelated base size — is
 * `applyBlockFontSizeStrut`'s job below (twin site:
 * `buildFontScaleDecorations`'s `taskItem` branch in
 * `textbox-editor.controller.ts`, for the EDIT overlay).
 */
export const CHECKLIST_BOX_SIZE = `${CHECKLIST_BOX_SIZE_FACTOR}em`;

/**
 * Vertical nudge for the checklist checkbox, in `em` (scales with the zoom-scaled
 * font). The box aligns to the text baseline but reads a touch too high, so it is
 * shifted DOWN via `position: relative; top`. Mirrored in `whiteboard.css`
 * (drift-guarded).
 */
export const CHECKLIST_BOX_OFFSET_Y = '0.15em';

/**
 * Bugreport 2026-08-16 (user) — at high zoom (~1000%) the checklist checkbox
 * looked visibly different between the EDIT overlay (thick border, rounded
 * corners) and the VIEW raster (thin border, square corners). Root cause:
 * both paths render a NATIVE `<input type="checkbox">`, relying entirely on
 * the browser's own theme for its border/corner-radius — but the EDIT
 * overlay achieves zoom via a CSS `transform: scale(...)` on an ancestor (the
 * checkbox is painted at its true small size, THEN blown up), while the VIEW
 * raster's SVG `<foreignObject>` is painted directly at the final zoomed
 * size. Native form-control themes do not scale border-width/corner-radius
 * linearly with control size, so "small, then transform-scaled" and
 * "painted large directly" land on visibly different proportions at extreme
 * zoom, even though neither path set an explicit border anywhere.
 *
 * Fix: stop relying on the native theme at all (`appearance: none`) and draw
 * the box entirely from these `em`-based values instead, which the browser's
 * CSS engine resolves identically regardless of which pipeline painted it.
 * Twin site: the `ul[data-type="taskList"] input[type="checkbox"]` rules in
 * whiteboard.css (drift-guarded unit test).
 */
export const CHECKLIST_BOX_BORDER_WIDTH = '0.09em';
export const CHECKLIST_BOX_BORDER_RADIUS = '0.2em';
export const CHECKLIST_BOX_BORDER_COLOR = '#9ca3af';
export const CHECKLIST_BOX_CHECKED_COLOR = '#2563eb';

/**
 * The checked-state checkmark glyph, as a `background-image` data URL — the
 * ONLY way to draw it for the VIEW raster: pseudo-elements (`::after`) exist
 * only in a live stylesheet-backed DOM, not in inline styles, and the SVG
 * `<foreignObject>` raster carries no stylesheet at all (see DD2). Using the
 * SAME data URL in both `applyChecklistInlineStyles` below AND the
 * `:checked` rule in whiteboard.css (drift-guarded) keeps the glyph
 * pixel-identical in both pipelines, rather than an inline `background-image`
 * here and an unrelated `::after` shape there that could silently diverge.
 */
// Double-quoted SVG attributes (not single-quoted) deliberately: `encodeURIComponent`
// escapes `"` to `%22` but leaves `'` untouched, so double-quoting here is what keeps
// the encoded URL free of literal quote characters inside the outer `url("...")`.
const CHECKLIST_CHECKMARK_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">' +
  '<path d="M5 13l4 4L19 7" stroke="white" stroke-width="3.5" fill="none" ' +
  'stroke-linecap="round" stroke-linejoin="round"/></svg>';
export const CHECKLIST_CHECKMARK_URL = `url("data:image/svg+xml,${encodeURIComponent(CHECKLIST_CHECKMARK_SVG)}")`;

// PRD-RTE-TIPTAP-document-model §TR6 / Task T3.2 — render the inner HTML for
// the textbox wrapper from `obj.document` via TipTap's `generateHTML`. Both
// the SVG-foreignObject view-mode renderer and the editor's mount element use
// this same function indirectly via `buildTextboxHtmlElement`, guaranteeing
// edit/view DOM parity. SSR/test-env fallback returns an empty string when
// the document has no content (matches legacy buildHtmlForTextbox behaviour
// for empty textboxes).
function renderDocumentHtml(obj: Readonly<TextBox>): string {
  const doc = obj.document;
  if (!doc || !Array.isArray(doc.content) || doc.content.length === 0) {
    return '';
  }
  // TipTap's `JSONContent` types `content` as a mutable array; our
  // `TextDocument` deliberately uses `readonly` arrays. Cast at the boundary —
  // `generateHTML` does not mutate the input.
  return generateHTML(doc as unknown as Parameters<typeof generateHTML>[0], TEXTBOX_EXTENSIONS);
}

/**
 * 2026-08-15 (Anke, "clean rewrite") — the obj-derived base styling shared by
 * BOTH the view-mode wrapper (`buildTextboxHtmlElement`, sized `100%`/`100%`
 * to fill its SVG raster container) and the edit-mode content wrapper
 * (`buildTextboxEditContentElement`, sized to the object's own WORLD-UNIT
 * `width`/`height` px so it can be scaled as a whole via a single ancestor
 * `transform: scale(...)` — see `TextboxEditorController#applyContentTransform`).
 * `size` is the only thing that differs between the two callers.
 */
function applyTextboxBaseStyles(
  el: HTMLElement,
  obj: Readonly<TextBox>,
  size: { readonly width: string; readonly height: string },
): void {
  const s = el.style;

  s.fontSize = `${obj.fontSize}px`;
  s.fontWeight = String(obj.fontWeight);
  s.color = obj.color;
  s.fontFamily = FONT_FAMILY;
  s.padding = `${obj.paddingV ?? 0}px ${obj.paddingH ?? 0}px`;
  s.lineHeight = String(EDIT_TEXTBOX_LINE_HEIGHT);
  s.wordBreak = 'break-word';
  s.overflow = 'hidden';
  s.boxSizing = 'border-box';
  s.width = size.width;
  s.height = size.height;
  s.margin = '0';

  s.textDecoration = obj.textDecoration ?? 'none';
  s.fontStyle = obj.fontStyle ?? 'normal';
  s.textTransform = obj.textTransform ?? 'none';

  // Vertical alignment via flexbox (PRD-RTE-PARITY §TR1).
  s.display = 'flex';
  s.flexDirection = 'column';
  s.justifyContent = VALIGN_LAYOUT[obj.vAlign]?.justifyContent ?? 'flex-start';
}

/**
 * How each `vAlign` is laid out: the flex `justifyContent` of the text column,
 * and the share of the free vertical space that ends up ABOVE the content
 * (0 / 0.5 / 1). The one table for the view raster, the edit overlay and the
 * hotspot/text measurement (`measureOverflow` releases the box height, so it
 * must add that share itself — Anke, 2026-10-03, review 0034).
 *
 * Deliberately NOT used by the SVG export (`core/export/textbox-to-svg-text.ts`):
 * it lays text out itself, without the DOM, and computes the same 0 / ½ / 1
 * share of the free space on its own (review 0034/03 — kept separate rather than
 * pulling `features/textbox` into `core/export`). Change both together.
 */
export const VALIGN_LAYOUT: Readonly<Record<VerticalAlign, { readonly justifyContent: string; readonly freeSpaceAbove: number }>> = {
  top: { justifyContent: 'flex-start', freeSpaceAbove: 0 },
  middle: { justifyContent: 'center', freeSpaceAbove: 0.5 },
  bottom: { justifyContent: 'flex-end', freeSpaceAbove: 1 },
};

/**
 * Build the canonical textbox HTML element. The returned `<div>` carries all
 * obj-derived inline styles (font, padding, alignment, decoration, vAlign
 * flex layout) and contains a single inner `<span>` text-wrapper that holds
 * the sanitized rich-text / list HTML produced by `buildHtmlForTextbox`.
 */
export function buildTextboxHtmlElement(obj: Readonly<TextBox>): HTMLElement {
  const el = document.createElement('div');
  applyTextboxBaseStyles(el, obj, { width: '100%', height: '100%' });

  // Inner text-wrapper carries horizontal alignment + super/sub script offset.
  const span = document.createElement('span');
  const ss = span.style;
  ss.textAlign = obj.hAlign;
  ss.display = 'block';
  if (obj.verticalScript === 'super' || obj.verticalScript === 'sub') {
    ss.verticalAlign = obj.verticalScript;
    ss.fontSize = '0.75em';
  }
  span.innerHTML = renderDocumentHtml(obj);
  fillEmptyParagraphs(span);
  applyBlockMarginReset(span);
  // MUST run before applyListMarkers: it looks for the <li>'s first REAL text
  // run to size the marker from. applyListMarkers inserts a synthetic marker
  // <span> as the <li>'s new first child — running after that would make the
  // marker's own (unmarked) text the "first run" instead, sizing every marker
  // at the object's base size regardless of the item's real font-size.
  applyListMarkerFontSize(span);
  applyListMarkers(span);
  applyBlockFontSizeStrut(span, obj.fontSize);
  applyChecklistInlineStyles(span);
  applyLinkPresentation(span);

  el.appendChild(span);
  return el;
}

/**
 * 2026-08-15 (Anke, "clean rewrite") — the edit overlay's INNER content
 * wrapper. Unlike `buildTextboxHtmlElement` (view path, `100%`/`100%` to fill
 * its raster container), this is sized to the object's own WORLD-UNIT
 * `width`/`height` in real px — i.e. UNSCALED, matching the persisted
 * document's font-size marks 1:1. The caller (`TextboxEditorController`)
 * mounts the live TipTap `Editor` directly into this element and applies a
 * single `transform: scale(viewport.scale)` to it, so the browser's own
 * cascade — not hand-multiplied JS — handles every descendant's on-screen
 * size at once, for any zoom level. No inner `<span>`/static content: TipTap
 * owns and populates this element's DOM directly.
 */
export function buildTextboxEditContentElement(obj: Readonly<TextBox>): HTMLDivElement {
  const el = document.createElement('div');
  applyTextboxBaseStyles(el, obj, { width: `${obj.width}px`, height: `${obj.height}px` });
  return el;
}

// PRD-RTE-TIPTAP-FIX §RC-B / Task T2.2 — TipTap's default schema emits browser
// block elements (`<p>`, `<ul>`, `<ol>`, `<li>`) whose user-agent default
// margins (`1em` top/bottom on `<p>`, padding/margin on lists) bleed into both
// the edit-mode ProseMirror surface and the view-mode SVG foreignObject. We
// neutralise them via inline `style.margin = '0'` on every emitted block
// element. DD2 favours inline styles over an embedded `<style>` tag because
// SVG serialization across user agents is more reliable for inline styles.
function applyBlockMarginReset(span: HTMLElement): void {
  const blocks = span.querySelectorAll('p, ul, ol, li');
  for (const el of Array.from(blocks)) {
    (el as HTMLElement).style.margin = '0';
  }
}

// Give generic bullet / ordered lists an `em`-based left indent (LIST_INDENT) so
// the gutter scales with the zoom-scaled font in the SVG raster, instead of the
// browser's fixed 40px UA default which stays constant under zoom. Excludes the
// taskList (its gutter is 0 — the checkbox replaces the marker, handled by
// `applyChecklistInlineStyles`). Twin site: the `.ProseMirror` list rules in
// whiteboard.css (drift-guarded).
//
// Bugreport 2026-08-15 (user, follow-up): the marker ("1.", "2.", the bullet
// dot) must sit FLUSH-LEFT with plain body text — the list as a whole is NOT
// indented — only the ITEM TEXT after the marker is, by `LIST_INDENT`.
//
// Bugreport 2026-08-16 (user): two native `list-style-position` approaches
// were tried and rejected — both confirmed empirically with throwaway
// Playwright renders before landing this fix:
//   - `outside` (the browser default) with the indent moved onto the `<li>`:
//     an `outside` marker is NOT drawn inside the `<li>`'s own padding box;
//     it is drawn to the left of the `<li>`'s BORDER edge, using room the
//     PARENT (`<ul>`/`<ol>`) provides via ITS OWN padding-left (the classic
//     reason the UA default puts `padding-left: 40px` on the list container,
//     not the item). With that container padding zeroed for the flush-marker
//     requirement, the marker had nowhere to render and drew off-screen —
//     invisible.
//   - `inside`: the marker becomes an ordinary inline atom inside the `<li>`'s
//     own principal box — but TipTap wraps each item's content in a
//     block-level `<p>`, and an inline marker immediately before a block
//     child gets placed in its OWN anonymous line, ABOVE the paragraph, not
//     beside it (also confirmed empirically — the `<li>` came out exactly
//     TWO line-heights tall for one line of text).
//
// Fix: stop relying on the native marker entirely. Suppress it
// (`list-style: none`, done by the caller/CSS) and render a literal marker
// `<span>`, ABSOLUTELY positioned (`<li>` gets `position: relative`, the
// marker gets `position: absolute; left: 0; top: 0`) so it sits fixed at the
// `<li>`'s top-left regardless of how much/what kind of content follows (one
// paragraph, several paragraphs, a nested list — all of it just flows
// normally under the `<li>`'s own `padding-left: LIST_INDENT`). This also
// sidesteps the flex-row alternative's own failure mode: a flex row only
// ever lays out TWO children (marker, text) correctly — a `listItem` with
// more than one block child (multi-paragraph, or a nested list) would push
// those extra children into new flex COLUMNS beside the text instead of
// stacking them below it.
// The marker glyph reuses `CHECKLIST_BOX_SIZE`/`CHECKLIST_ITEM_GAP` as its
// own box-width/gap — the SAME shared factor the checklist's checkbox+gap
// already uses, not a fourth independently-tuned pair of numbers.
function applyListMarkers(span: HTMLElement): void {
  const lists = span.querySelectorAll('ul:not([data-type="taskList"]), ol');
  for (const listEl of Array.from(lists)) {
    const list = listEl as HTMLElement;
    list.style.paddingLeft = '0';
    list.style.listStyle = 'none';
    const isOrdered = list.tagName === 'OL';
    // `generateHTML` emits an `ol start="N"` attribute when the document's
    // orderedList node carries a non-default `start` (e.g. pasted content) —
    // honour it so the raster's numbering matches the persisted document.
    const start = isOrdered ? parseInt(list.getAttribute('start') ?? '1', 10) || 1 : 1;
    const items = Array.from(list.children).filter(
      (child): child is HTMLLIElement => child.tagName === 'LI',
    );
    items.forEach((li, index) => {
      li.style.position = 'relative';
      li.style.paddingLeft = LIST_INDENT;
      const marker = document.createElement('span');
      marker.style.position = 'absolute';
      marker.style.left = '0';
      marker.style.top = '0';
      marker.style.width = CHECKLIST_BOX_SIZE;
      marker.style.textAlign = 'right';
      marker.textContent = isOrdered ? `${start + index}.` : '•';
      li.insertBefore(marker, li.firstChild);
    });
  }
}

// Anke, 2026-07-19 — link presentation.
//
// Two jobs, both of which must happen on the VIEW element as inline styles (the
// SVG raster carries no stylesheet):
//
//  1. Strip the browser's link look. The URL text is NOT clickable any more, so
//     leaving it blue-and-underlined would invite clicks that do nothing. It
//     reads as ordinary text; that it is an address is already obvious from the
//     "https://".
//  2. Append the launcher icon after each link. THIS is the clickable part. It
//     carries the href on a data attribute so the measuring pass
//     (`measureLinkIcons`) can pair each on-screen rectangle with its URL.
//
// Twin site: the `.ProseMirror a` rules in whiteboard.css do (1) for the edit
// overlay, and `linkIconDecorations` in `textbox-editor.controller.ts` does (2).
function applyLinkPresentation(span: HTMLElement): void {
  for (const a of Array.from(span.querySelectorAll<HTMLAnchorElement>('a[href]'))) {
    a.style.color = 'inherit';
    a.style.textDecoration = 'none';
    a.style.cursor = 'inherit';

    const icon = document.createElement('span');
    icon.setAttribute(LINK_ICON_ATTR, a.getAttribute('href') ?? '');
    icon.textContent = LINK_ICON_GLYPH;
    // `inline-block` so the measuring pass gets a real box for it.
    icon.style.display = 'inline-block';
    icon.style.fontSize = linkIconFontSize(a);
    icon.style.padding = `0 ${LINK_ICON_GAP}`;
    icon.style.verticalAlign = 'baseline';
    a.insertAdjacentElement('afterend', icon);
  }
}

/**
 * The launcher's font-size, as a CSS value.
 *
 * Anke, 2026-07-19 — the icon sat at one fixed size while its link text could be
 * any size. The naive fix (`0.85em`) does NOT work: the icon is a SIBLING of the
 * `<a>`, so its `em` resolves against the PARAGRAPH, not against the link's own
 * run. A 40px URL therefore still got a paragraph-sized arrow.
 *
 * So the run's size is looked up explicitly and baked in as px. Inside the
 * anchor first (the per-run `textStyle` mark renders as a `<span>` in there),
 * then the anchor itself, then upwards — the first inline `font-size` wins.
 * Nothing found means the link runs at the object's own size, and `em` is then
 * exactly right.
 *
 * Emitting px here is deliberate and safe: `scaleRasterFontSizeMarks` multiplies
 * every absolute px by the zoom, so the icon scales with the raster like the
 * text it belongs to. An `em` would be skipped by that pass — which is what we
 * want in the fallback case, since it inherits from an already-scaled parent.
 */
function linkIconFontSize(a: HTMLAnchorElement): string {
  const inner = a.querySelector<HTMLElement>('[style*="font-size"]');
  const own = a.style.fontSize;
  let raw = inner?.style.fontSize || own || '';
  if (!raw) {
    for (let el = a.parentElement; el && !raw; el = el.parentElement) {
      raw = el.style.fontSize || '';
    }
  }
  const px = /^([\d.]+)px$/.exec(raw.trim());
  return px ? `${parseFloat(px[1]!) * LINK_ICON_SCALE}px` : `${LINK_ICON_SCALE}em`;
}

// The `::marker` glyph (bullet disc / ordinal number) is drawn by the browser and
// inherits the `<li>`'s OWN font-size — NOT the size of the `<span>` runs nested
// inside it. A per-run `textStyle.fontSize` mark therefore resizes the item text
// while the marker stays pinned to the object-level font-size, so an enlarged list
// shows big text next to a tiny number. Propagate the FIRST run's size onto the
// `<li>` so the marker tracks the text it labels (first-run wins on a mixed-size
// item — matching Word/Docs, and it keeps the marker column steady when a word
// mid-sentence is emphasised). Items whose first run carries no size mark are left
// untouched so they keep inheriting the object font-size.
// Scoped to marker-bearing lists ONLY: a taskList has no `::marker` (a checkbox
// replaces it), so first-run marker-sizing does not apply to it — that is what
// "out of scope HERE" means. The `<li>`'s own font-size for a taskItem IS still
// set, deliberately and by a DIFFERENT function with different sizing logic
// (largest run, not first run, so the checkbox's `em`-width tracks the item's
// tallest text): see `applyBlockFontSizeStrut`'s `li[data-type="taskItem"]`
// loop below.
// Twin site: `buildFontScaleDecorations` in `textbox-editor.controller.ts` does
// the same for the EDIT overlay (via a `Decoration.node` on the listItem).
function applyListMarkerFontSize(span: HTMLElement): void {
  const items = span.querySelectorAll('ul:not([data-type="taskList"]) > li, ol > li');
  for (const li of Array.from(items)) {
    const size = firstRunFontSize(li as HTMLElement);
    if (size) (li as HTMLElement).style.fontSize = size;
  }
}

// The inline `font-size` carried by the FIRST text run inside `li`, or `''` when
// that run is unsized. Deliberately NOT `querySelector('[style*=font-size]')`:
// that would pick up a sized run LATER in the item and let an emphasised word
// mid-sentence drive the marker even though the item starts at the object size.
// Mirrors the latch in `firstRunFontSizePx` (textbox-editor.controller.ts).
function firstRunFontSize(li: HTMLElement): string {
  const walk = (node: Node): string | null => {
    if (node.nodeType === Node.TEXT_NODE) {
      if ((node.textContent ?? '') === '') return null;
      // Found the first run — climb back to the <li>, taking the nearest
      // inline font-size on the way (the textStyle mark's <span>).
      for (let el = node.parentElement; el && el !== li; el = el.parentElement) {
        if (el.style.fontSize) return el.style.fontSize;
      }
      return '';
    }
    for (const child of Array.from(node.childNodes)) {
      const hit = walk(child);
      if (hit !== null) return hit;
    }
    return null;
  };
  return walk(li) ?? '';
}

// 2026-08-15 (Anke, "clean rewrite") — convert a laid-out block element's DOM
// subtree into the same `StrutJsonNode` shape `ProseMirrorNode.toJSON()`
// produces, so the ONE shared algorithm in `textbox-strut-fix.ts`
// (`maxRunFontSizePxFromJson`) can run unchanged against the view-mode
// raster too. A text node's "mark" is read by climbing its ancestor chain up
// to `root` for the nearest inline `font-size` — the per-run `textStyle`
// mark's own `<span style="font-size:...">` — mirroring `firstRunFontSize`'s
// climb below, just not stopping at the first hit. A `<br>` becomes a
// `hardBreak` sized the same way (Anke, 2026-10-02): a marked break sizes its
// line like a run does.
export function domRunsToStrutJson(root: HTMLElement): StrutJsonNode {
  function fontSizeAt(start: HTMLElement | null): string | null {
    for (let el = start; el && el !== root; el = el.parentElement) {
      if (el.style.fontSize) return el.style.fontSize;
    }
    return null;
  }
  function convert(node: Node): StrutJsonNode {
    if (node.nodeType === Node.TEXT_NODE) {
      const fontSize = fontSizeAt(node.parentElement);
      return {
        type: 'text',
        ...(fontSize ? { marks: [{ type: 'textStyle', attrs: { fontSize } }] } : {}),
      };
    }
    if (node.nodeName === 'BR') {
      // A hard break sizes its line like a run (see `isSizedLeaf`).
      const fontSize = fontSizeAt(node.parentElement);
      return {
        type: 'hardBreak',
        ...(fontSize ? { marks: [{ type: 'textStyle', attrs: { fontSize } }] } : {}),
      };
    }
    return { type: 'element', content: Array.from(node.childNodes).map(convert) };
  }
  return convert(root);
}

// Bugreport 2026-08-15 (Anke) — the VIEW-mode raster twin of the edit
// overlay's paragraph/heading strut fix (`maxRunFontSizePx` in
// `textbox-editor.controller.ts`). A `<p>`/`<h*>` is itself an inline
// formatting context and generates its own line-box "strut" from ITS OWN
// font-size (CSS3 §10.8), independent of whatever smaller size its child
// runs carry — a fact that applies to the raster exactly as much as to the
// live editor. This was the one half of the fix that was missing: edit mode
// got it, the raster never did, which is why edit and view mode kept
// disagreeing on line spacing for a uniformly-resized paragraph. Sets each
// block's own `font-size` to its largest marked run or hard break, in raw
// world units — `scaleRasterFontSizeMarks` (textbox-svg-renderer.ts) already
// walks every `[style*="font-size"]` descendant afterwards and multiplies by
// `scale`, so this value is picked up and scaled automatically, no changes
// needed there.
//
// Also sizes `li[data-type="taskItem"]` itself (a later bugreport, same day):
// the checklist checkbox's `em`-based width (`CHECKLIST_BOX_SIZE`) resolves
// against the NEAREST ancestor with an explicit font-size — the checkbox
// lives in a `<label>` that is a SIBLING of the text `<p>`, not a
// descendant of it, so without this the `<li>` never got its own font-size
// and every checkbox sat at the textbox's unrelated base size regardless of
// the item's real (possibly per-run-mixed) size. Twin site:
// `buildFontScaleDecorations`'s `taskItem` branch in
// `textbox-editor.controller.ts` does the same for the EDIT overlay. The
// `<li>` loop needs no unmarked-run protection (see below): a taskItem's
// content model always wraps a nested `<p>`, which is itself corrected by
// the FIRST loop before this one runs, so it already carries its own
// explicit font-size and never cascades the `<li>`'s.
function applyBlockFontSizeStrut(span: HTMLElement, baseFontSizePx: number): void {
  for (const block of Array.from(span.querySelectorAll('p, h1, h2, h3, h4, h5, h6'))) {
    const el = block as HTMLElement;
    const level = maxRunFontSizePxFromJson(domRunsToStrutJson(el));
    if (level === null) continue;
    protectUnmarkedRunsFromCascade(el, `${baseFontSizePx}px`);
    el.style.fontSize = `${level}px`;
  }
  for (const item of Array.from(span.querySelectorAll('li[data-type="taskItem"]'))) {
    const level = maxRunFontSizePxFromJson(domRunsToStrutJson(item as HTMLElement));
    if (level !== null) (item as HTMLElement).style.fontSize = `${level}px`;
  }
}

// Bugreport 2026-08-16 (follow-up) — setting a block's own font-size to its
// largest marked run or break (the CSS §10.8 strut fix above) has an unintended side
// effect: any UNMARKED run in the same block (plain text with no
// `textStyle.fontSize` of its own) has no explicit font-size of its own
// either, so it starts inheriting the block's NEW, larger size via the CSS
// cascade instead of the textbox's actual base size. Real workflow: select
// only PART of a paragraph and enlarge it — the rest of the paragraph must
// not visibly grow along with it. Fix: before overwriting the block's own
// font-size, give every so-far-unmarked text run an explicit font-size of
// its own (pinned to `baseFontSizePx`, i.e. what it inherited before this
// function ran), so it stops participating in the cascade from the block.
// Must run BEFORE the block's font-size is overwritten below, or every run
// would already read as "marked" via the block's own new inline style.
function protectUnmarkedRunsFromCascade(block: HTMLElement, baseFontSizePx: string): void {
  const doc = block.ownerDocument;
  const hasOwnFontSize = (node: Node): boolean => {
    for (let el = node.parentElement; el && el !== block; el = el.parentElement) {
      if (el.style.fontSize) return true;
    }
    return false;
  };
  // Snapshot BEFORE mutating: wrapping a text node changes its own parent
  // chain, which would otherwise confuse a live walk.
  const walker = doc.createTreeWalker(block, NodeFilter.SHOW_TEXT);
  const unmarked: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.textContent?.trim() === '') continue; // whitespace-only, nothing to protect
    if (!hasOwnFontSize(node)) unmarked.push(node as Text);
  }
  for (const textNode of unmarked) {
    const wrapper = doc.createElement('span');
    wrapper.style.fontSize = baseFontSizePx;
    textNode.replaceWith(wrapper);
    wrapper.appendChild(textNode);
  }
}

// An empty `<p></p>` (a blank line the user added with Enter) collapses to ZERO
// height in the SVG-foreignObject raster, so blank lines vanish in view mode
// even though they show in the edit overlay. TipTap/ProseMirror gives an empty
// paragraph a line box via a trailing `<br class="ProseMirror-trailingBreak">`;
// `generateHTML` does NOT emit it. Mirror that here so an empty paragraph gets
// exactly one line's height in the raster (and in the overflow measurement),
// restoring edit/view parity for blank lines. Skips paragraphs that already
// carry content or a `<br>` (no double line).
function fillEmptyParagraphs(span: HTMLElement): void {
  for (const p of Array.from(span.querySelectorAll('p'))) {
    if (p.textContent?.trim() === '' && p.querySelector('br, img') === null) {
      p.appendChild(document.createElement('br'));
    }
  }
}

// O2D.W-PRD-040 §TR1 — checklist inline layout for the SVG-raster view path.
// External CSS cannot reach the `<foreignObject>` raster (see DD2 above), so the
// checkbox-replaces-bullet + inline-text-with-1em-gap layout is applied as INLINE
// styles here. DEDICATED queries scoped to `[data-type="taskList"]`/`taskItem`
// are kept OUT of the generic `p, ul, ol, li` margin loop so the marker
// suppression cannot strip disc/numeral markers from generic bullet/ordered
// lists (FC3). The EDIT path mirrors these values in `whiteboard.css` (TR2/TR3).
function applyChecklistInlineStyles(span: HTMLElement): void {
  // Part 1 — suppress the user-agent disc marker on the taskList only.
  for (const ul of Array.from(span.querySelectorAll('ul[data-type="taskList"]'))) {
    const s = (ul as HTMLElement).style;
    s.listStyle = 'none';
    s.paddingLeft = '0';
  }
  // Part 2 — lay the checkbox + text inline with a 1em gap.
  for (const li of Array.from(span.querySelectorAll('li[data-type="taskItem"]'))) {
    const s = (li as HTMLElement).style;
    s.display = 'flex';
    s.alignItems = 'baseline';
    s.gap = CHECKLIST_ITEM_GAP;
  }
  // The content `<div>` grows and wraps long text without overflowing the flex row.
  for (const div of Array.from(span.querySelectorAll('li[data-type="taskItem"] > div'))) {
    const s = (div as HTMLElement).style;
    s.flex = '1';
    s.minWidth = '0';
  }
  // Part 3 — size the native checkbox in `em` so it scales with the zoom-scaled
  // font. `font-size: inherit` is REQUIRED first: an `<input>` keeps its own UA
  // font-size, so `1em` would otherwise resolve against that fixed size and stay
  // constant under zoom. `flex-shrink: 0` keeps the box from being squeezed.
  for (const input of Array.from(
    span.querySelectorAll<HTMLInputElement>('li[data-type="taskItem"] input[type="checkbox"]'),
  )) {
    const s = input.style;
    s.fontSize = 'inherit';
    s.width = CHECKLIST_BOX_SIZE;
    s.height = CHECKLIST_BOX_SIZE;
    s.margin = '0';
    s.flexShrink = '0';
    // Nudge the box down so it sits better against the text baseline.
    s.position = 'relative';
    s.top = CHECKLIST_BOX_OFFSET_Y;
    // Bugreport 2026-08-16 (user) — draw the box entirely from CSS instead of
    // the native theme (see CHECKLIST_BOX_BORDER_WIDTH's doc comment above
    // for why): `appearance: none` first, `box-sizing: border-box` so the
    // border is inset within the already-set `em` width/height rather than
    // adding to it.
    s.appearance = 'none';
    s.boxSizing = 'border-box';
    s.border = `${CHECKLIST_BOX_BORDER_WIDTH} solid ${
      input.checked ? CHECKLIST_BOX_CHECKED_COLOR : CHECKLIST_BOX_BORDER_COLOR
    }`;
    s.borderRadius = CHECKLIST_BOX_BORDER_RADIUS;
    s.backgroundColor = input.checked ? CHECKLIST_BOX_CHECKED_COLOR : '#fff';
    if (input.checked) {
      s.backgroundImage = CHECKLIST_CHECKMARK_URL;
      s.backgroundSize = '70%';
      s.backgroundPosition = 'center';
      s.backgroundRepeat = 'no-repeat';
    }
  }
}
